import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import './InformationPage.css';

const INFO_SECTIONS = [
  {
    key: 'shipping',
    icon: '🚚',
    title: 'Shipping & Delivery',
    content: [
      { heading: 'Delivery Timeline', text: 'Standard delivery takes 3–7 business days across India.' },
      { heading: 'Free Shipping', text: 'Enjoy free shipping on all orders.' },
      { heading: 'Packaging', text: 'All orders are carefully packed in our signature jewellery boxes with tissue paper and a thank-you card. Gift wrapping is available on request.' },
      { heading: 'Tracking', text: 'Once your order is shipped, you will receive a tracking link via email and SMS. You can also track your order in the "Current Orders" section of your account.' },
      { heading: 'International Shipping', text: 'We currently ship only within India. International shipping is coming soon — stay tuned!' },
    ]
  },
  {
    key: 'helpdesk',
    icon: '🎧',
    title: 'Help Desk',
    content: [
      { heading: 'Contact Us', text: 'Our customer support team is available Monday to Saturday, 9 AM to 9 PM IST. Reach us via email at abhirasjewellerycustomerquery@gmail.com or call +91 99123 69733 / +91 63804 11386.' },
      { heading: 'Response Time', text: 'We aim to respond to all queries within 4 business hours. Complex issues may take up to 24 hours to resolve.' },
      { heading: 'Common Issues', text: 'For damaged products, wrong items received, or payment issues — please raise a support ticket within 48 hours of delivery.' },
      { heading: 'Live Chat', text: 'Live chat support is available during business hours via our Instagram handle @abhirasjewellery.' },
    ]
  },
  {
    key: 'eco',
    icon: '🌿',
    title: 'Eco-Friendly',
    content: [
      { heading: 'Sustainable Packaging', text: 'We use 100% recycled and biodegradable packaging materials. Our jewellery boxes are made from FSC-certified paper and are fully recyclable.' },
      { heading: 'Responsible Sourcing', text: 'All metals and gemstones used in our jewellery are sourced from ethical, conflict-free suppliers who adhere to fair labour practices.' },
      { heading: 'Carbon Offset', text: 'We partner with certified carbon offset programs to neutralise the carbon footprint of every delivery.' },
      { heading: 'Reduce Waste', text: 'Our designs are made to last — we believe in slow jewellery, not fast fashion. Quality over quantity, always.' },
    ]
  },
  {
    key: 'india',
    icon: '🇮🇳',
    title: 'Made in India',
    content: [
      { heading: 'Artisan Craftsmanship', text: 'Every piece of Abhiras jewellery is handcrafted by skilled artisans across India, particularly from the jewellery hubs of Jaipur, Kolkata, and Mumbai.' },
      { heading: 'Supporting Local', text: 'We work directly with small and medium artisan workshops, ensuring fair wages and safe working conditions for over 200 craftspeople.' },
      { heading: 'Celebrating Heritage', text: 'Our designs are inspired by India\'s rich artistic traditions — from Kundan and Meenakari to contemporary minimalist styles — blending heritage with modernity.' },
      { heading: 'Quality Assurance', text: 'All products undergo a rigorous 12-point quality check before dispatch to ensure you receive only the finest jewellery.' },
    ]
  },
  {
    key: 'aesthetic',
    icon: '✨',
    title: 'Aesthetic Design',
    content: [
      { heading: 'Curated Collections', text: 'Each collection at Abhiras is thoughtfully curated by our in-house design team, drawing inspiration from nature, architecture, and global jewellery trends.' },
      { heading: 'Timeless Pieces', text: 'We design jewellery that transcends seasons and trends — pieces you will treasure and pass down through generations.' },
      { heading: 'Custom Orders', text: 'Want something truly unique? We offer bespoke jewellery design services. Contact us to discuss your dream piece.' },
      { heading: 'Photography', text: 'All product images are shot in natural light to give you the most accurate representation of colour, texture, and size.' },
    ]
  },
  {
    key: 'resolution',
    icon: '⚡',
    title: 'Fast Resolution',
    content: [
      { heading: '24-Hour Commitment', text: 'We promise to resolve all customer issues within 24 business hours — no ifs, no buts. Customer satisfaction is our top priority.' },
      { heading: 'Dedicated Support', text: 'Our support team is always ready to help with any questions about your order, delivery, or products.' },
    ]
  },
];

export default function InformationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || '';
  const sectionRefs = useRef({});

  useEffect(() => {
    if (section && sectionRefs.current[section]) {
      setTimeout(() => {
        sectionRefs.current[section].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [section]);

  return (
    <div className="page-container">
      <div className="info-page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={20}/></button>
        <span className="info-page-logo" onClick={() => navigate('/')}>Abhiras</span>
      </div>

      <div className="info-page-hero">
        <h1>Everything You Need to Know</h1>
        <p>Shipping, support, sustainability and more — all in one place.</p>
      </div>

      {/* Quick nav */}
      <div className="info-quick-nav">
        {INFO_SECTIONS.map(s => (
          <button key={s.key} className={`info-quick-btn${section === s.key ? ' active' : ''}`}
            onClick={() => sectionRefs.current[s.key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            <span>{s.icon}</span>
            <span>{s.title}</span>
          </button>
        ))}
      </div>

      <div className="info-sections">
        {INFO_SECTIONS.map(sec => (
          <div
            key={sec.key}
            ref={el => sectionRefs.current[sec.key] = el}
            className={`info-section-block${section === sec.key ? ' highlighted' : ''}`}
          >
            <div className="info-sec-header">
              <span className="info-sec-icon">{sec.icon}</span>
              <h2>{sec.title}</h2>
            </div>
            <div className="info-sec-content">
              {sec.content.map((item, i) => (
                <div key={i} className="info-content-item">
                  <h3>{item.heading}</h3>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <footer className="footer">
        <div className="footer-logo">Abhiras Jewellery</div>
        <p>© {new Date().getFullYear()} Abhiras Jewellery. All rights reserved.</p>
        <p>Crafted with ♥ in India</p>
      </footer>
    </div>
  );
}
